import { cancelRagUploads, commitRagUploads, detectRagUploadConflicts, extractFilenameFromPath, getIndexedRagFiles, mapRagProgressStage, } from "./ragUploadService";
/**
 * Shared base for host-specific RAG upload services.
 * Hosts provide transport (backendClient progress stream vs direct WebSocket),
 * while this base centralizes progress parsing, handler fanout, and upload APIs.
 */
export class RagUploadServiceBase {
    constructor(options) {
        this.progressHandlers = new Set();
        this.serviceName = options.serviceName;
        this.progressUpdateType = options.progressUpdateType || "rag_file_progress";
        this.getBaseUrl = options.getBaseUrl;
        this.getAuthHeader = options.getAuthHeader;
        this.refreshExternalFiles = options.refreshExternalFiles;
    }
    onProgress(handler) {
        this.progressHandlers.add(handler);
    }
    offProgress(handler) {
        this.progressHandlers.delete(handler);
    }
    processProgressEnvelope(update) {
        try {
            const progress = this.parseProgressEnvelope(update);
            if (!progress) {
                return null;
            }
            this.notifyHandlers(progress);
            if (progress.stage === "complete" && this.refreshExternalFiles) {
                this.refreshExternalFiles().catch((error) => {
                    console.warn(`[${this.serviceName}] Error refreshing external files cache:`, error);
                });
            }
            return progress;
        }
        catch (error) {
            console.warn(`[${this.serviceName}] Failed to process progress envelope:`, error);
            return null;
        }
    }
    async detectUploadConflicts(files) {
        return detectRagUploadConflicts({
            baseUrl: this.getBaseUrl(),
            files,
            authHeader: this.getAuthHeader(),
        });
    }
    async commitUploads(uploadId, decisions, workbookId) {
        return commitRagUploads({
            baseUrl: this.getBaseUrl(),
            uploadId,
            decisions,
            workbookId,
            authHeader: this.getAuthHeader(),
        });
    }
    async cancelUploads(filepaths) {
        await cancelRagUploads({
            baseUrl: this.getBaseUrl(),
            filepaths,
            authHeader: this.getAuthHeader(),
        });
        if (this.refreshExternalFiles) {
            await this.refreshExternalFiles();
        }
    }
    async getIndexedFiles() {
        try {
            return await getIndexedRagFiles({
                baseUrl: this.getBaseUrl(),
                authHeader: this.getAuthHeader(),
            });
        }
        catch (error) {
            console.warn(`[${this.serviceName}] Failed to fetch indexed files:`, error);
            return [];
        }
    }
    parseProgressEnvelope(update) {
        if (!update || typeof update !== "object") {
            return null;
        }
        const envelope = update;
        const updateType = (typeof envelope.update_type === "string" ? envelope.update_type : undefined) ||
            (typeof envelope.type === "string" ? envelope.type : undefined);
        const updateData = envelope.update_data ?? envelope.data;
        if (updateType !== this.progressUpdateType || !updateData || typeof updateData !== "object") {
            return null;
        }
        const data = updateData;
        const filepath = typeof data.filepath === "string" ? data.filepath : "";
        return {
            filepath,
            filename: extractFilenameFromPath(filepath),
            current: Number(data.current || 0),
            total: Number(data.total || 0),
            stage: mapRagProgressStage(String(data.stage || "uploading")),
            progress: Number(data.progress || 0),
            error: typeof data.error === "string" && data.error.trim().length > 0 ? data.error : undefined,
        };
    }
    notifyHandlers(progress) {
        this.progressHandlers.forEach((handler) => {
            try {
                handler(progress);
            }
            catch (error) {
                console.warn(`[${this.serviceName}] Error in progress handler:`, error);
            }
        });
    }
}
